import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles, Upload, ImageOff, RefreshCw } from 'lucide-react';
import { api, AiLimitError, type BibleEntry } from '../lib/api';
import { toast } from '../lib/toast';
import { cn } from '../lib/utils';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { StylePicker } from './style-picker';

/** Center-crop + resize an image file to a square-ish dataURL (max `size`px). */
function resizeImageToDataUrl(file: File, size = 640): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const ratio = img.width / img.height;
        const w = ratio >= 1 ? size : Math.round(size * ratio);
        const h = ratio >= 1 ? Math.round(size / ratio) : size;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no-ctx');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image-load-failed'));
    };
    img.src = url;
  });
}

interface EntryImageStudioProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  entry: BibleEntry;
  onUpdated: (entry: BibleEntry) => void;
}

/**
 * Consistent image studio for a Story-Bible entry: generate/regenerate with AI
 * (using the entry's canon + the project's art style + optional art direction),
 * upload a custom image, remove it, and see the monthly image limit — all in one
 * responsive modal/drawer. The chosen art style is the project-wide "Estilo
 * visual" (persisted), so it permeates every image in the book/manga.
 */
export function EntryImageStudio({ open, onOpenChange, projectId, entry, onUpdated }: EntryImageStudioProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'gen' | 'upload' | 'remove' | null>(null);
  const [notes, setNotes] = useState('');
  const [style, setStyle] = useState('');
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Load the project's current art style + the image quota when the studio opens.
  useEffect(() => {
    if (!open) return;
    setNotes('');
    api.getProject(projectId).then((r) => setStyle(r.project.mangaStyle ?? '')).catch(() => {});
    api.getAiUsage().then((u) => setUsage(u.cover)).catch(() => {});
  }, [open, projectId]);

  const unlimited = !usage || usage.limit < 0 || usage.limit >= 100000;
  const remaining = usage ? usage.limit - usage.used : Infinity;
  const outOfCredits = !unlimited && remaining <= 0;

  // Persist the art style as the project-wide setting (fire and forget) so it
  // truly permeates every generation, not just this one.
  const handleStyleChange = (next: string) => {
    setStyle(next);
    api.updateProject(projectId, { mangaStyle: next }).catch(() => {});
  };

  const handleGenerate = async () => {
    setBusy('gen');
    try {
      const { entry: updated } = await api.generateBibleEntryImage(projectId, entry.id, {
        prompt: notes.trim() || undefined,
        style: style.trim() || undefined,
      });
      onUpdated(updated);
      api.getAiUsage().then((u) => setUsage(u.cover)).catch(() => {});
      toast.success(t('storyBible.imageGenerated', 'Imagem gerada!'));
    } catch (e) {
      if (e instanceof AiLimitError) {
        toast.error(t('imageStudio.limitReached', 'Limite de imagens atingido este mês.'));
      } else {
        const msg = (e as { message?: string })?.message;
        toast.error(
          msg === 'cover_limit'
            ? t('imageStudio.limitReached', 'Limite de imagens atingido este mês.')
            : t('storyBible.imageGenerateError', 'Falha ao gerar imagem (verifique sua cota de imagens).')
        );
      }
      api.getAiUsage().then((u) => setUsage(u.cover)).catch(() => {});
    } finally {
      setBusy(null);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('storyBible.selectImageFile', 'Selecione um arquivo de imagem.'));
      return;
    }
    setBusy('upload');
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      const { entry: updated } = await api.updateBibleEntry(projectId, entry.id, { imageUrl: dataUrl });
      onUpdated(updated);
      toast.success(t('storyBible.imageUploaded', 'Imagem enviada!'));
    } catch {
      toast.error(t('storyBible.imageUploadError', 'Falha ao enviar a imagem.'));
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    setBusy('remove');
    try {
      const { entry: updated } = await api.updateBibleEntry(projectId, entry.id, { imageUrl: null });
      onUpdated(updated);
      toast.success(t('imageStudio.removed', 'Imagem removida.'));
    } catch {
      toast.error(t('imageStudio.removeError', 'Falha ao remover a imagem.'));
    } finally {
      setBusy(null);
    }
  };

  const anyBusy = busy !== null;

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="sm:max-w-lg">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('imageStudio.title', 'Imagem de {{name}}', { name: entry.name })}</ResponsiveModalTitle>
          <ResponsiveModalDescription>
            {t('imageStudio.subtitle', 'Gere com IA (segue o estilo de arte do projeto), envie a sua, ou troque quando quiser.')}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <div className="grid gap-4 py-2">
          {/* Preview */}
          <div className="relative mx-auto w-full max-w-[260px]">
            <div className="relative aspect-square overflow-hidden rounded-2xl border border-border bg-muted/40">
              {entry.imageUrl ? (
                <img src={entry.imageUrl} alt={entry.name} className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full w-full place-items-center bg-[linear-gradient(135deg,#7EA4F5,#5B8DEF)] font-serif text-5xl font-semibold text-[#070B16]">
                  {entry.name.charAt(0).toUpperCase()}
                </div>
              )}
              {busy === 'gen' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm">
                  <div className="relative grid place-items-center">
                    <span className="absolute h-14 w-14 animate-ping rounded-full bg-primary/20" />
                    <Loader2 className="relative animate-spin text-primary" size={28} />
                  </div>
                  <p className="max-w-[80%] text-center text-sm font-medium text-foreground">
                    {t('imageStudio.generating', 'Gerando… pode levar até 1 min')}
                  </p>
                  <div className="flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary" />
                  </div>
                </div>
              )}
            </div>
            {entry.imageUrl && (
              <button
                onClick={handleRemove}
                disabled={anyBusy}
                className="mx-auto mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive disabled:opacity-40"
              >
                {busy === 'remove' ? <Loader2 size={12} className="animate-spin" /> : <ImageOff size={12} />}
                {t('imageStudio.remove', 'Remover imagem')}
              </button>
            )}
          </div>

          {/* Art style — the project-wide "Estilo visual" */}
          <div className="grid gap-1.5">
            <Label>{t('imageStudio.styleLabel', 'Estilo de arte do projeto')}</Label>
            <StylePicker value={style} onChange={handleStyleChange} disabled={anyBusy} />
            <p className="text-[11px] text-muted-foreground">
              {t('imageStudio.styleHint', 'Este estilo é aplicado a todas as ilustrações e personagens do projeto.')}
            </p>
          </div>

          {/* Optional art direction */}
          <div className="grid gap-1.5">
            <Label htmlFor="img-notes">{t('imageStudio.notesLabel', 'Detalhes para a imagem (opcional)')}</Label>
            <Textarea
              id="img-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('imageStudio.notesPlaceholder', 'Ex: close no rosto, expressão séria, luz de fim de tarde…')}
              rows={2}
              className="resize-none"
            />
          </div>

          {/* Limits */}
          <div className={cn('rounded-lg border px-3 py-2 text-xs', outOfCredits ? 'border-destructive/40 bg-destructive/5 text-destructive' : 'border-border bg-muted/30 text-muted-foreground')}>
            {usage
              ? unlimited
                ? t('imageStudio.usageUnlimited', 'Imagens: ilimitadas no seu plano.')
                : outOfCredits
                  ? t('imageStudio.usageOut', 'Limite de imagens atingido este mês ({{used}}/{{limit}}). Faça upgrade para gerar mais.', { used: usage.used, limit: usage.limit })
                  : t('imageStudio.usage', 'Imagens geradas este mês: {{used}}/{{limit}}.', { used: usage.used, limit: usage.limit })
              : t('imageStudio.usageLoading', 'Carregando limite de imagens…')}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleGenerate} disabled={anyBusy || outOfCredits} className="flex-1 min-w-[160px]">
              {busy === 'gen'
                ? <Loader2 size={15} className="mr-2 animate-spin" />
                : entry.imageUrl
                  ? <RefreshCw size={15} className="mr-2" />
                  : <Sparkles size={15} className="mr-2" />}
              {entry.imageUrl ? t('imageStudio.regenerate', 'Regenerar com IA') : t('imageStudio.generate', 'Gerar com IA')}
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={anyBusy}>
              {busy === 'upload' ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Upload size={15} className="mr-2" />}
              {t('imageStudio.upload', 'Enviar imagem')}
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
